// MaturaRightTriangleTwoTrigClosedTask.js
// Zadanie maturalne zamknięte za 2 punkty: dwa pytania ABCD do jednego rysunku.

import React, { useContext } from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";
import { ThemeContext } from "../../../../context/ThemeContext";

const LETTERS = ["A", "B", "C", "D"];

const randInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const pick = (arr) => arr[randInt(0, arr.length - 1)];

function shuffle(array) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = randInt(0, i);
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function gcd(a, b) {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) [x, y] = [y, x % y];
  return x || 1;
}

function frac(n, d) {
  const g = gcd(n, d);
  return { kind: "frac", n: n / g, d: d / g };
}

function sqrtDen(n, rad) {
  let factor = 1;
  for (let s = Math.floor(Math.sqrt(rad)); s >= 2; s -= 1) {
    if (rad % (s * s) === 0 && n % s === 0) {
      factor = s;
      break;
    }
  }
  return { kind: "sqrtDen", n: n / factor, rad: rad / (factor * factor) };
}

function valueKey(v) {
  if (v.kind === "sqrtDen") return `${v.n}/sqrt(${v.rad})`;
  return `${v.n}/${v.d}`;
}

function valueLatex(v) {
  if (v.kind === "sqrtDen") {
    if (v.n === 1) return `\\frac{1}{\\sqrt{${v.rad}}}`;
    return `\\frac{${v.n}}{\\sqrt{${v.rad}}}`;
  }
  if (v.d === 1) return String(v.n);
  return `\\frac{${v.n}}{${v.d}}`;
}

function uniqueValues(values) {
  const seen = new Set();
  const out = [];
  values.forEach((v) => {
    const key = valueKey(v);
    if (!seen.has(key)) {
      seen.add(key);
      out.push(v);
    }
  });
  return out;
}

function makeOptions(correct, distractors) {
  const values = uniqueValues([correct, ...distractors]);
  let filled = values;
  let k = 1;
  while (filled.length < 4) {
    filled = uniqueValues([...filled, frac(k, k + 1), frac(k + 1, k + 2)]);
    k += 1;
  }

  const correctKey = valueKey(correct);
  return shuffle(filled.slice(0, 4)).map((value, idx) => ({
    label: LETTERS[idx],
    value,
    latex: valueLatex(value),
    isCorrect: valueKey(value) === correctKey,
  }));
}

function trigValues({ ad, ac, cd, ab }) {
  const bigHypSq = ab * ab + ac * ac;
  return {
    alpha: {
      sin: frac(ac, cd),
      cos: frac(ad, cd),
      tan: frac(ac, ad),
    },
    beta: {
      sin: sqrtDen(ac, bigHypSq),
      cos: sqrtDen(ab, bigHypSq),
      tan: frac(ac, ab),
    },
  };
}

function labelForFn(fn) {
  if (fn === "sin") return "\\sin";
  if (fn === "cos") return "\\cos";
  return "\\operatorname{tg}";
}

function polishFn(fn) {
  if (fn === "sin") return "Sinus";
  if (fn === "cos") return "Cosinus";
  return "Tangens";
}

function makeQuestion({ angle, fn, correct, distractors }) {
  const options = makeOptions(correct, distractors);
  return {
    angle,
    fn,
    options,
    correctIndex: options.findIndex((opt) => opt.isCorrect),
  };
}

function RightTriangleWithMedianDiagram({ ad, ac, cd, ab }) {
  const { theme } = useContext(ThemeContext);
  const textColor = theme === "light" ? "#000" : "#fff";
  const fill = theme === "light" ? "#f9f9f9" : "#000";
  const lengthColor = theme === "light" ? "#b45309" : "#f59e0b";
  const labelBg = theme === "light" ? "rgba(249,249,249,0.82)" : "rgba(0,0,0,0.72)";

  const scale = Math.min(34, 300 / ab, 170 / ac);
  const ax = 58;
  const topPadding = 36;
  const ay = topPadding + ac * scale;
  const cx = ax;
  const cy = topPadding;
  const dx = ax + ad * scale;
  const dy = ay;
  const bx = ax + ab * scale;
  const by = ay;
  const width = Math.max(280, bx + 70);
  const height = ay + 54;

  const unit = (vx, vy) => {
    const len = Math.hypot(vx, vy) || 1;
    return { x: vx / len, y: vy / len };
  };
  const alphaBase = unit(ax - dx, ay - dy);
  const alphaSide = unit(cx - dx, cy - dy);
  const betaBase = unit(ax - bx, ay - by);
  const betaSide = unit(cx - bx, cy - by);
  const alphaBisector = unit(alphaBase.x + alphaSide.x, alphaBase.y + alphaSide.y);
  const betaBisector = unit(betaBase.x + betaSide.x, betaBase.y + betaSide.y);
  const alphaCos = Math.max(-1, Math.min(1, alphaBase.x * alphaSide.x + alphaBase.y * alphaSide.y));
  const betaCos = Math.max(-1, Math.min(1, betaBase.x * betaSide.x + betaBase.y * betaSide.y));
  const alphaAngle = Math.acos(alphaCos);
  const betaAngle = Math.acos(betaCos);
  const alphaNarrow = Math.max(0, Math.min(1, (0.9 - alphaAngle) / 0.55));
  const betaNarrow = Math.max(0, Math.min(1, (0.85 - betaAngle) / 0.5));
  const alphaDist = 18 + alphaNarrow * 24;
  const betaDist = 20 + betaNarrow * 34;
  const alphaLabelX = dx + alphaBisector.x * alphaDist - 7 - alphaNarrow * 10;
  const alphaRawY = dy + alphaBisector.y * alphaDist - 12 + alphaNarrow * 3;
  const alphaLabelY = Math.min(alphaRawY, dy - 18 - alphaNarrow * 4);
  const betaLabelX = bx + betaBisector.x * betaDist - 7 - betaNarrow * 16;
  const betaRawY = by + betaBisector.y * betaDist - 12 + betaNarrow * 3;
  const betaLabelY = Math.min(betaRawY, by - 20 - betaNarrow * 5);
  const cdMidX = (cx + dx) / 2;
  const cdMidY = (cy + dy) / 2;
  const cdNormal = unit(dy - cy, cx - dx);
  const cdLabelX = cdMidX + cdNormal.x * 20 - 11;
  const cdLabelY = cdMidY + cdNormal.y * 20 - 12;
  const rightMark = Math.min(22, Math.max(16, scale * 0.7));

  return (
    <svg viewBox={`0 0 ${width} ${height}`} width={width} height={height} style={{ display: "block", width: "100%", maxWidth: width, height: "auto", margin: "0 auto" }}>
      <polygon
        points={`${ax},${ay} ${bx},${by} ${cx},${cy}`}
        fill={fill}
        stroke={textColor}
        strokeWidth="2"
      />
      <line x1={cx} y1={cy} x2={dx} y2={dy} stroke={textColor} strokeWidth="2" />

      <path d={`M ${ax + rightMark} ${ay} A ${rightMark} ${rightMark} 0 0 0 ${ax} ${ay - rightMark}`} fill="none" stroke={textColor} strokeWidth="1.6" />
      <circle cx={ax + rightMark * 0.42} cy={ay - rightMark * 0.42} r="2.8" fill={textColor} />

      {[
        ["A", ax - 10, ay + 22],
        ["B", bx - 4, by + 22],
        ["C", cx - 8, cy - 12],
        ["D", dx - 4, dy - 11],
      ].map(([label, x, y]) => (
        <foreignObject key={label} x={x} y={y - 16} width="28" height="24">
          <div style={{ color: textColor, fontSize: 15 }}>
            <InlineMath math={label} />
          </div>
        </foreignObject>
      ))}

      <foreignObject x={(ax + bx) / 2 - 16} y={ay + 4} width="44" height="24">
        <div style={{ color: textColor, textAlign: "center" }}>
          <InlineMath math={String(ab)} />
        </div>
      </foreignObject>
      <foreignObject x={cdLabelX} y={cdLabelY} width="42" height="24">
        <div style={{ color: lengthColor, background: labelBg, borderRadius: 4, padding: "0 2px", display: "inline-block" }}>
          <InlineMath math={String(cd)} />
        </div>
      </foreignObject>
      <foreignObject x={alphaLabelX} y={alphaLabelY} width="28" height="24">
        <div style={{ color: textColor, fontSize: 15 }}>
          <InlineMath math={"\\alpha"} />
        </div>
      </foreignObject>
      <foreignObject x={betaLabelX} y={betaLabelY} width="28" height="24">
        <div style={{ color: textColor, fontSize: 15 }}>
          <InlineMath math={"\\beta"} />
        </div>
      </foreignObject>
    </svg>
  );
}

function fixedOptions(values, correctIndex) {
  return values.map((value, idx) => ({
    label: LETTERS[idx],
    value,
    latex: valueLatex(value),
    isCorrect: idx === correctIndex,
  }));
}

function generateMay2025Original() {
  const data = { ad: 3, ac: 4, cd: 5, ab: 6 };
  const trig = trigValues(data);
  return {
    type: "MaturaRightTriangleTwoTrigClosedTask",
    data: {
      ...data,
      trig,
      q1: {
        angle: "alpha",
        fn: "tan",
        options: fixedOptions(
          [frac(2, 3), frac(3, 4), frac(4, 5), frac(4, 3)],
          3
        ),
        correctIndex: 3,
      },
      q2: {
        angle: "beta",
        fn: "sin",
        options: [
          {
            label: "A",
            value: sqrtDen(4, 52),
            latex: "\\frac{2}{\\sqrt{13}}",
            isCorrect: true,
          },
          {
            label: "B",
            value: sqrtDen(3, 13),
            latex: "\\frac{3}{\\sqrt{13}}",
            isCorrect: false,
          },
          {
            label: "C",
            value: { kind: "sqrtDen", n: 5, rad: 52 },
            latex: "\\frac{5}{2\\sqrt{13}}",
            isCorrect: false,
          },
          {
            label: "D",
            value: frac(4, 5),
            latex: "\\frac{4}{5}",
            isCorrect: false,
          },
        ],
        correctIndex: 0,
      },
      maxPoints: 2,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_MAJ",
      taskNumber: 18,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (
    generationOptions.variant === "original" &&
    generationOptions.session === "2025_MAJ"
  ) {
    return generateMay2025Original();
  }

  // Zachowujemy konstrukcję liczbową oryginału CKE. Dla AD:AC:CD = 3:4:5
  // oraz AB = 2·AD otrzymujemy BC = 2k√13, więc odpowiedzi upraszczają się
  // do małych ilorazów zamiast przypadkowych pierwiastków typu √601.
  const scale = pick([1, 2, 3, 4]);
  const base = { ad: 3 * scale, ac: 4 * scale, cd: 5 * scale };
  const ab = 2 * base.ad;
  const data = { ...base, ab };
  const trig = trigValues(data);

  const fns = shuffle(["sin", "cos", "tan"]);
  const alphaFn = fns[0];
  const betaFn = fns[1];

  const alphaDistractors = [
    trig.alpha.sin,
    trig.alpha.cos,
    trig.alpha.tan,
    trig.beta.sin,
    trig.beta.cos,
    trig.beta.tan,
  ].filter((v) => valueKey(v) !== valueKey(trig.alpha[alphaFn]));

  const betaDistractors = [
    trig.beta.sin,
    trig.beta.cos,
    trig.beta.tan,
    trig.alpha.sin,
    trig.alpha.cos,
    trig.alpha.tan,
  ].filter((v) => valueKey(v) !== valueKey(trig.beta[betaFn]));

  return {
    type: "MaturaRightTriangleTwoTrigClosedTask",
    data: {
      ...data,
      trig,
      q1: makeQuestion({
        angle: "alpha",
        fn: alphaFn,
        correct: trig.alpha[alphaFn],
        distractors: alphaDistractors,
      }),
      q2: makeQuestion({
        angle: "beta",
        fn: betaFn,
        correct: trig.beta[betaFn],
        distractors: betaDistractors,
      }),
      maxPoints: 2,
    },
  };
}

function renderOptions(options) {
  return (
    <div style={{ marginTop: 8 }}>
      {options.map((opt) => (
        <div key={opt.label} style={{ marginBottom: 10 }}>
          <InlineMath math={`${opt.label}.\\; ${opt.latex}`} />
        </div>
      ))}
    </div>
  );
}

function questionText(q) {
  const angleLatex = q.angle === "alpha" ? "\\alpha" : "\\beta";
  return `${labelForFn(q.fn)} ${angleLatex}`;
}

export function renderTask({ data }) {
  const { ab, cd, q1, q2 } = data;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne - zamknięte (0-2 pkt).</strong>
      </p>
      <p>
        Dany jest trójkąt prostokątny <InlineMath math={"ABC"} />, w którym bok{" "}
        <InlineMath math={"BC"} /> jest przeciwprostokątną, przyprostokątna{" "}
        <InlineMath math={"AB"} /> ma długość <InlineMath math={String(ab)} />, a
        środkowa <InlineMath math={"CD"} /> ma długość{" "}
        <InlineMath math={String(cd)} />.
      </p>
      <p>
        Oznaczmy kąt <InlineMath math={"ADC"} /> przez{" "}
        <InlineMath math={"\\alpha"} />, natomiast kąt{" "}
        <InlineMath math={"ABC"} /> przez <InlineMath math={"\\beta"} />.
      </p>
      <RightTriangleWithMedianDiagram {...data} />

      <p>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</p>
      <p>
        <strong>1.</strong> <InlineMath math={questionText(q1)} /> jest równy:
      </p>
      {renderOptions(q1.options)}

      <p>
        <strong>2.</strong> <InlineMath math={questionText(q2)} /> jest równy:
      </p>
      {renderOptions(q2.options)}
    </div>
  );
}

function renderChoiceInput(q, value, onChange, disabled) {
  return (
    <ABCDInput
      options={q.options.map((opt, idx) => ({
        value: idx,
        label: opt.label,
        textNode: opt.label,
      }))}
      value={value}
      onChange={onChange}
      disabled={disabled}
    />
  );
}

export function renderInput({ data }, userAnswer = {}, onChange, disabled) {
  const ua = userAnswer || {};
  const setChoice = (key, q) => (idx) => {
    const opt = q.options[idx];
    onChange?.({
      ...ua,
      [key]: {
        choiceIndex: idx,
        label: opt.label,
        latex: opt.latex,
        value: opt.value,
      },
    });
  };

  return (
    <div style={{ display: "grid", gap: 18 }}>
      <div>
        <strong>Odpowiedź 1:</strong>
        {renderChoiceInput(data.q1, ua.q1?.choiceIndex, setChoice("q1", data.q1), disabled)}
      </div>
      <div>
        <strong>Odpowiedź 2:</strong>
        {renderChoiceInput(data.q2, ua.q2?.choiceIndex, setChoice("q2", data.q2), disabled)}
      </div>
    </div>
  );
}

export function checkAnswer({ data }, userAnswer = {}) {
  const q1Ok = userAnswer?.q1?.choiceIndex === data.q1.correctIndex;
  const q2Ok = userAnswer?.q2?.choiceIndex === data.q2.correctIndex;
  const score = (q1Ok ? 1 : 0) + (q2Ok ? 1 : 0);
  return {
    isCorrect: score === 2,
    score,
    maxScore: 2,
    parts: {
      "pytanie 1": q1Ok,
      "pytanie 2": q2Ok,
    },
  };
}

export function renderAnswer({ data }) {
  const c1 = data.q1.options[data.q1.correctIndex];
  const c2 = data.q2.options[data.q2.correctIndex];
  return (
    <div style={{ display: "grid", gap: 4 }}>
      <div>
        <strong>1.</strong> <InlineMath math={`${c1.label}.\\; ${c1.latex}`} />
      </div>
      <div>
        <strong>2.</strong> <InlineMath math={`${c2.label}.\\; ${c2.latex}`} />
      </div>
    </div>
  );
}

export function renderExplanation({ data }) {
  const { ad, ac, cd, ab, q1, q2 } = data;
  const bigHypSq = ab * ab + ac * ac;
  const c1 = q1.options[q1.correctIndex];
  const c2 = q2.options[q2.correctIndex];

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1.</strong> Ponieważ <InlineMath math="CD" /> jest środkową,
        punkt <InlineMath math="D" /> jest środkiem boku <InlineMath math="AB" />.
        Następnie korzystamy z twierdzenia Pitagorasa w trójkącie <InlineMath math="ACD" />:
      </p>
      <BlockMath math={`AD=\\frac{AB}{2}=\\frac{${ab}}{2}=${ad}`} />
      <BlockMath math={`AC=\\sqrt{CD^2-AD^2}=\\sqrt{${cd}^2-${ad}^2}=${ac}`} />
      <BlockMath
        math={`\\begin{aligned}\\sin\\alpha&=\\frac{${ac}}{${cd}},\\quad \\cos\\alpha=\\frac{${ad}}{${cd}},\\\\ \\operatorname{tg}\\alpha&=\\frac{${ac}}{${ad}}\\end{aligned}`}
      />

      <p>
        <strong>Krok 2.</strong> Z trójkąta prostokątnego{" "}
        <InlineMath math={"ABC"} />:
      </p>
      <BlockMath math={`BC=\\sqrt{${ab}^2+${ac}^2}=\\sqrt{${bigHypSq}}`} />
      <BlockMath
        math={`\\begin{aligned}\\sin\\beta&=\\frac{${ac}}{\\sqrt{${bigHypSq}}},\\quad \\cos\\beta=\\frac{${ab}}{\\sqrt{${bigHypSq}}},\\\\ \\operatorname{tg}\\beta&=\\frac{${ac}}{${ab}}\\end{aligned}`}
      />

      <p>
        <strong>Odpowiedzi:</strong>
      </p>
      <BlockMath math={`${questionText(q1)}=${c1.latex}`} />
      <BlockMath math={`${questionText(q2)}=${c2.latex}`} />
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer) return <i>(brak odpowiedzi)</i>;
  const q1 = userAnswer.q1;
  const q2 = userAnswer.q2;
  return (
    <div style={{ marginTop: "1rem", display: "grid", gap: 4 }}>
      <div>
        1.{" "}
        {q1?.label ? (
          <InlineMath math={`${q1.label}.\\; ${q1.latex}`} />
        ) : (
          <i>(brak)</i>
        )}
      </div>
      <div>
        2.{" "}
        {q2?.label ? (
          <InlineMath math={`${q2.label}.\\; ${q2.latex}`} />
        ) : (
          <i>(brak)</i>
        )}
      </div>
    </div>
  );
}
